// Rug Room Shots: "Add a style" dialog (Rooms tab, SPEC 10). SU (Sunroom) is a fixed style since the
// 2026-10-21 release, so the backend refuses it here (409 style_exists / name_exists).
// POST /styles { code, name, note } adds a hand-picked style. It is insert-only: a style can
// never be renamed or removed later, and the Style Sorter never picks it by itself (a rug only
// gets it from Change style on the plan or Reject -> Wrong style in Review).
// Errors: 409 style_exists / name_exists / worker_not_ready carry the server's message.
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, CircularProgress, Stack,
} from '@mui/material';
import rrsApi from './api';
import { primaryButtonSx, fmtInt, errText } from './common';

const CODE_RE = /^[A-Z]{2}$/;
const NAME_RE = /^[A-Za-z0-9 &'-]{2,32}$/;
const SERVER_MESSAGE_CODES = ['style_exists', 'name_exists', 'worker_not_ready'];

const AddStyleDialog = ({ open, onClose, initialCode, config, notify, onAdded }) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Every open starts clean, with the code prefilled from "Define SU" when given.
  useEffect(() => {
    if (!open) return;
    setCode(String(initialCode || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2));
    setName('');
    setNote('');
    setError('');
    setBusy(false);
  }, [open, initialCode]);

  const features = (config && config.features) || {};
  const cleanName = name.trim().replace(/\s+/g, ' ');
  const codeOk = CODE_RE.test(code);
  const nameOk = NAME_RE.test(cleanName);
  const styles = (config && config.styles) || [];
  const codeTaken = codeOk && styles.some((s) => s.code === code);
  const nameTaken = nameOk && styles.some((s) => String(s.name).toLowerCase() === cleanName.toLowerCase());

  const submit = async () => {
    if (!codeOk || !nameOk || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await rrsApi.addStyle({ code, name: cleanName, note: note.trim() });
      const st = (res && res.style) || { code, name: cleanName };
      const n = (res && res.roomsWithCode) || 0;
      notify(`Added style ${st.name} (${st.code}). ${fmtInt(n)} room(s) with this code can now be activated.`, 'success');
      if (onAdded) onAdded(res);
      onClose();
    } catch (e) {
      const c = e && e.body ? e.body.code : null;
      setError(SERVER_MESSAGE_CODES.includes(c) ? (e.body.error || errText(e)) : errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a style</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          New styles are chosen by hand only. The Style Sorter knows Boho, California Rustic, Coastal, Modern Classic
          and Urban Modern (plus the Outdoor rule; Sunroom comes from the Outdoor &amp; sunroom list) and never picks a new
          style by itself. A rug gets the new style when someone sets it: Change style on the plan, or Reject → Wrong
          style in Review. Styles cannot be renamed or removed later.
        </Alert>
        {features.addStyle === false && features.addStyleReason !== 'loading' && (
          <Alert severity="info" sx={{ mb: 2 }}>The worker has not been updated for new styles yet.</Alert>
        )}
        <Stack spacing={2}>
          <TextField
            size="small" label="Code (2 letters)" value={code} autoFocus={!initialCode}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2))}
            error={(code.length > 0 && !codeOk) || codeTaken}
            helperText={codeTaken ? `Style code ${code} already exists.` : 'The first 2 letters of its room codes, e.g. KR for KR26LRB01.'}
            inputProps={{ maxLength: 2, style: { fontFamily: 'monospace', textTransform: 'uppercase' } }}
          />
          <TextField
            size="small" label="Name" value={name} autoFocus={!!initialCode}
            onChange={(e) => setName(e.target.value.slice(0, 40))}
            error={(name.length > 0 && !nameOk) || nameTaken}
            helperText={nameTaken
              ? `A style named ${cleanName} already exists.`
              : '2-32 characters: letters, digits, spaces, & \' -'}
          />
          <TextField
            size="small" label="Note (optional)" value={note} multiline minRows={2}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
          />
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained" onClick={submit} sx={primaryButtonSx}
          disabled={busy || !codeOk || !nameOk || codeTaken || nameTaken || features.addStyle === false}
        >
          {busy ? <CircularProgress size={20} color="inherit" /> : 'Add style'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStyleDialog;
